import { useRef, useEffect, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import confetti from 'canvas-confetti'
import Pill from '@/components/ui/Pill'
import Link from '@/components/ui/Link'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { easeMedium } from '@/lib/transitions'
import { trackAffiliateLinkClick } from '@/lib/analytics'
import { useDealContext } from '@/context/DealContext'

type DealIconProps = {
  icon: ReactNode
  animation: string
}

type DealLinkProps = {
  link: string
  passReferrerUrl?: boolean
  fullWidth?: boolean
  sponsored?: boolean
}

type DealClaimButtonProps = {
  onClick?: () => void
  claimed?: boolean
  label: string
  link: string
  passReferrerUrl?: boolean
  sponsored?: boolean
  background: string
  logo?: ReactNode
  buttonOnly?: boolean
  emailRequired?: boolean | null | undefined
  desktopIsRequired?: boolean | null | undefined
  promoCode?: string | null | undefined
  eligibility?: string | null | undefined
  allowsLogin?: boolean | null | undefined
}

type DealClaimProps = DealLinkProps &
  DealClaimButtonProps & {
    setClaimed?: (claimed: boolean) => void
    setTriggerClaim?: (trigger: boolean) => void
    menu?: ReactNode
    className?: string
    trigger?: boolean
  }

export default function DealClaim({
  link,
  passReferrerUrl,
  sponsored,
  label,
  logo,
  background,
  menu,
  buttonOnly,
  emailRequired,
  desktopIsRequired,
  promoCode,
  eligibility,
  allowsLogin,
  className,
}: DealClaimProps) {
  const { claimed, handleClaim } = useDealContext()

  return (
    <div className={cn(className, 'flex flex-col items-center gap-3')}>
      <div
        className={cn('flex flex-wrap items-center justify-center gap-y-3 md:flex-nowrap md:gap-0')}
      >
        <ClaimButton
          onClick={() => handleClaim(buttonOnly, emailRequired, desktopIsRequired)}
          claimed={claimed}
          label={label}
          link={link}
          passReferrerUrl={passReferrerUrl}
          sponsored={sponsored}
          background={background}
          logo={logo}
          buttonOnly={buttonOnly}
          emailRequired={emailRequired}
        />

        <div className="order-3 flex w-fit flex-wrap items-center justify-center gap-y-3 xs:order-2">
          <AnimatePresence>
            {claimed && !buttonOnly && (
              <DealLink
                key="deal-link"
                link={link}
                passReferrerUrl={passReferrerUrl}
                sponsored={sponsored}
              />
            )}
            {claimed && !buttonOnly && promoCode && (
              <DealPromoCode key="deal-promo-code" promoCode={promoCode} />
            )}
          </AnimatePresence>
        </div>

        {menu && <div className="order-2 ml-3 xs:order-3">{menu}</div>}
      </div>

      {!buttonOnly && (
        <AnimatePresence mode="wait">
          {claimed ? (
            // div, not p: Icon renders a <div> wrapper and <p> cannot contain
            // block elements (React hydration error)
            <motion.div
              key="claimed"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="absolute top-full flex flex-wrap items-center justify-center gap-1 whitespace-normal pt-3 text-center text-12 leading-normal text-error-300 sm:-bottom-10 sm:text-15"
            >
              Sign up{allowsLogin ? ' or login' : ''} with the link above
              <Icon name="arrow-turn-up" size={11} className="pb-1" />
              {emailRequired ? (
                `(deal emailed after signup)`
              ) : promoCode ? (
                <>
                  (use code <span className="font-medium text-white">{promoCode}</span> at checkout)
                </>
              ) : (
                `(deal applies automatically)`
              )}
            </motion.div>
          ) : eligibility ? (
            <motion.p
              key="eligibility"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="absolute top-full pt-3 text-12 text-white/60 sm:-bottom-10 sm:text-15"
            >
              {eligibility}
            </motion.p>
          ) : null}
        </AnimatePresence>
      )}
    </div>
  )
}

export function DealIcon({ icon, animation }: DealIconProps) {
  return (
    <div
      className={cn(
        'absolute inset-0 flex items-center justify-center text-white transition-transform duration-300 ease-in-out',
        animation,
      )}
    >
      {icon}
    </div>
  )
}

export function ClaimButton({
  onClick,
  claimed,
  label,
  link,
  passReferrerUrl,
  sponsored,
  background,
  logo,
  buttonOnly,
  emailRequired,
}: DealClaimButtonProps): ReactNode {
  const { setDealTracked } = useDealContext()

  const confirmationText = `Deal Almost Claimed`
  const labelRef = useRef<HTMLSpanElement>(null)
  const confirmationRef = useRef<HTMLSpanElement>(null)
  const [buttonWidth, setButtonWidth] = useState(0)
  const [allowHover, setAllowHover] = useState(false)

  useEffect(() => {
    const currentRef = claimed ? confirmationRef.current : labelRef.current

    if (currentRef) {
      setButtonWidth(currentRef.offsetWidth)
    }

    if (claimed) {
      const confettiTimer = setTimeout(() => {
        confetti({
          particleCount: 240,
          startVelocity: 30,
          spread: 360,
          origin: {
            x: 0.5,
            y: 0.25,
          },
          colors: ['10B981'],
        })
        setAllowHover(true)
      }, 300)
      return () => clearTimeout(confettiTimer)
    }
  }, [claimed, label])

  const pill = (
    <Pill
      // Once claimed, this pill becomes the content of the "Visit Site" <Link>.
      // Keeping the claim handler attached here means the click that opens the
      // deal link ALSO re-runs handleClaim → a duplicate deal_form_submission.
      // Drop it when claimed so the claimed state only ever navigates.
      onClick={claimed ? undefined : onClick}
      size="large"
      style="gradient"
      icon={true}
      as={claimed ? 'span' : 'button'}
      className={cn(
        'group order-1 transition-all active:scale-95',
        !buttonOnly && (claimed ? 'bg-white hover:bg-[#10B981]' : 'gap-0 pr-1'),
      )}
    >
      <div
        style={{ background: claimed ? '#10B981' : background }}
        className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full border border-black/10 transition-colors duration-300 ease-in-out"
      >
        <DealIcon
          icon={<Icon name="circle-half-dotted-check" />}
          animation={`transition-opacity ${claimed ? 'scale-100 group-hover:opacity-60' : 'scale-0'}`}
        />
        {logo && <DealIcon icon={logo} animation={claimed ? 'scale-0' : 'scale-100'} />}
      </div>

      <div
        className="relative h-10 overflow-hidden transition-all duration-300 ease-in-out"
        style={{ width: `${buttonWidth}px` }}
      >
        <div
          className={cn(
            'relative top-0 flex h-fit w-fit flex-col transition-transform duration-300 ease-in-out will-change-transform',
            claimed ? '-translate-y-10' : 'translate-y-0',
            allowHover ? 'group-hover:translate-y-0' : '',
          )}
        >
          <span
            ref={labelRef}
            className={cn(
              'flex h-10 w-fit items-center whitespace-nowrap pl-2 pr-2.5',
              claimed ? 'w-full justify-center gap-1.5' : '',
            )}
          >
            {claimed ? (
              <>
                <span>Visit Site</span>
                <Icon name="arrow-right" />
              </>
            ) : (
              label
            )}
          </span>
          <span ref={confirmationRef} className="flex h-10 w-fit items-center whitespace-nowrap">
            {confirmationText}
          </span>
        </div>
      </div>
    </Pill>
  )

  return claimed ? (
    <Link
      href={link}
      target="_blank"
      onClick={() => setDealTracked(true)}
      className="inline-flex w-fit shrink-0"
      passReferrerUrl={passReferrerUrl}
      sponsored={sponsored}
    >
      {pill}
    </Link>
  ) : (
    pill
  )
}

export function DealLink({ link, passReferrerUrl, fullWidth = false, sponsored }: DealLinkProps) {
  const { setDealTracked } = useDealContext()

  const linkRef = useRef<HTMLDivElement>(null)
  const [linkWidth, setLinkWidth] = useState(0)
  const [copied, setCopied] = useState(false)

  const convertedLink = link.replace(/^https?:\/\//, '')

  useEffect(() => {
    if (linkRef.current) {
      setLinkWidth(linkRef.current.offsetWidth + 12)
    }
  }, [])

  const copyLink = () => {
    setCopied(true)
    navigator.clipboard.writeText(link)
    trackAffiliateLinkClick(link)
    setDealTracked(true)

    const anchor = document.createElement('a')
    anchor.href = link
    anchor.target = '_blank'
    if (!passReferrerUrl) {
      anchor.referrerPolicy = 'no-referrer'
      anchor.rel = 'noopener noreferrer'
    } else {
      anchor.rel = 'noopener'
    }
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  return (
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: fullWidth ? '100%' : linkWidth }}
      exit={{ width: 0 }}
      transition={easeMedium}
      className="flex cursor-pointer justify-end overflow-hidden"
    >
      <div
        ref={linkRef}
        className={cn(
          'animated-border relative flex h-10 w-fit items-center rounded-full text-white',
          fullWidth ? 'w-full' : 'w-fit',
        )}
      >
        <Link
          href={link}
          target="_blank"
          onClick={() => setDealTracked(true)}
          className="gap-0 transition-colors hover:text-emerald-500"
          passReferrerUrl={passReferrerUrl}
          sponsored={sponsored}
        >
          <div className="flex h-10 w-10 animate-pulse items-center justify-center pl-1">
            <Icon name="arrow-right" className="text-emerald-500" />
          </div>
          <span className="truncate">{convertedLink}</span>
        </Link>
        <div
          onClick={copyLink}
          className={cn(
            'ml-auto flex h-10 w-10 items-center justify-center pr-0.5 transition-all',
            copied ? 'text-emerald-500 opacity-100' : 'opacity-40 hover:opacity-100',
          )}
        >
          {copied ? <Icon name="check" /> : <Icon name="clone" />}
        </div>
      </div>
    </motion.div>
  )
}

export function DealPromoCode({
  promoCode,
  fullWidth = false,
  highlight = false,
  locked = false,
}: {
  promoCode: string
  fullWidth?: boolean
  highlight?: boolean
  locked?: boolean
}) {
  const codeRef = useRef<HTMLDivElement>(null)
  const [codeWidth, setCodeWidth] = useState(0)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (codeRef.current) {
      setCodeWidth(codeRef.current.offsetWidth + 12)
    }
  }, [])

  const copyPromoCode = async () => {
    if (locked) return
    try {
      await navigator.clipboard.writeText(promoCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: fullWidth ? '100%' : codeWidth }}
      exit={{ width: 0 }}
      transition={easeMedium}
      className="flex justify-end overflow-hidden"
    >
      <div
        ref={codeRef}
        onClick={copyPromoCode}
        className={cn(
          'group relative flex h-10 cursor-pointer items-center rounded-full text-white',
          fullWidth ? 'w-full' : 'w-fit',
          highlight ? 'animated-border' : 'border border-white/20',
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center pl-1">
          <Icon name="circle-hashtag" className="text-emerald-500" />
        </div>
        <span className={cn('truncate', locked && 'select-none blur-[3px]')}>{promoCode}</span>
        <div
          className={cn(
            'ml-auto flex h-10 w-10 items-center justify-center pr-0.5 transition-all',
            copied ? 'text-emerald-500 opacity-100' : 'opacity-40 group-hover:opacity-100',
          )}
        >
          {!locked && copied ? <Icon name="check" /> : <Icon name="clone" />}
        </div>
      </div>
    </motion.div>
  )
}
